//@ts-nocheck
import { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { GraphQLClient, ClientError } from "graphql-request";
import { FetchArgs, FetchBaseQueryArgs } from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import { RequestDocument, Variables } from "graphql-request/dist/types"

/* Incorporated from @reduxjs  query/utils for simplicity */

function isAbsoluteUrl(url: string) {
  return new RegExp(`(^|:)//`).test(url)
}

const withoutTrailingSlash = (url: string) => url.replace(/\/$/, '')
const withoutLeadingSlash = (url: string) => url.replace(/^\//, '')

function joinUrls(
  base: string | undefined,
  url: string | undefined
): string {
  if (!base) {
    return url!
  }
  if (!url) {
    return base
  }

  if (isAbsoluteUrl(url)) {
    return url
  }

  base = withoutTrailingSlash(base)
  url = withoutLeadingSlash(url)

  return `${base}/${url}`
}

/*!
 * is-plain-object <https://github.com/jonschlinkert/is-plain-object>
 *
 * Copyright (c) 2014-2017, Jon Schlinkert.
 * Released under the MIT License.
 */


function isObject(o: any): boolean {
  return Object.prototype.toString.call(o) === '[object Object]';
}

function isPlainObject(o: any): o is object {
  var ctor, prot;

  if (isObject(o) === false) return false;

  // If has modified constructor
  ctor = o.constructor;
  if (ctor === undefined) return true;

  // If has modified prototype
  prot = ctor.prototype;
  if (isObject(prot) === false) return false;

  // If constructor does not have an Object-specific method
  if (prot.hasOwnProperty('isPrototypeOf') === false) {
    return false;
  }

  // Most likely a plain Object
  return true;
};

function stripUndefined(obj: any) {
  if (!isPlainObject(obj)) {
    return obj
  }
  const copy: Record<string, any> = { ...obj }
  for (const [k, v] of Object.entries(copy)) {
    if (typeof v === 'undefined') delete copy[k]
  }
  return copy
}
export type GraphqlFetchBaseQueryArgs = { client?: GraphQLClient } & Omit<FetchBaseQueryArgs, "fetchFn">;




/**
 * This is a very small wrapper around fetch that aims to simplify GraphQL requests. 
 * Adopted from fetchBaseQuery from RTK ToolKit
 *
 * @example
 * ```ts
 * const baseQuery = fetchBaseQuery({
 *   baseUrl: 'https://api.your-really-great-app.com/v1/',
 *   prepareHeaders: (headers, { getState }) => {
 *     const token = (getState() as RootState).auth.token;
 *     // If we have a token set in state, let's assume that we should be passing it.
 *     if (token) {
 *       headers.set('authorization', `Bearer ${token}`);
 *     }
 *     return headers;
 *   },
 * })
 * ```
 *
 * @param {string} baseUrl
 * The base URL for an API service.
 * Typically in the format of http://example.com/
 *
 * @param {(headers: Headers, api: { getState: () => unknown }) => Headers} prepareHeaders
 * An optional function that can be used to inject headers on requests.
 * Provides a Headers object, as well as the `getState` function from the
 * redux store. Can be useful for authentication.
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Headers
 *
 * @param {(input: RequestInfo, init?: RequestInit | undefined) => Promise<Response>} fetchFn
 * Accepts a custom `fetch` function if you do not want to use the default on the window.
 * Useful in SSR environments if you need to use a library such as `isomorphic-fetch` or `cross-fetch`
 *
 */

export interface GraphQLFetchArgs extends FetchArgs {
  document: RequestDocument;
  variables?: Variables | undefined;
}
export interface GraphQLRequestInit extends RequestInit {
  document: RequestDocument;
  variables?: Variables | undefined;
}

export function graphqlFetchBaseQuery({
  baseUrl,
  prepareHeaders = (x) => x,
  client,
  ...baseFetchOptions
}: GraphqlFetchBaseQueryArgs): BaseQueryFn<string | GraphQLFetchArgs, unknown, Pick<ClientError, "name" | "message" | "stack">, Partial<Pick<ClientError, "request" | "response">>> {
  return async (arg, { signal, getState, endpoint, forced, type }) => {
     let {
      url,
      method = 'GET' as const,
      headers = new Headers({}),
      body = undefined,
      document = "",
      variables = undefined,
      ...rest
    } = typeof arg == 'string' ? { url: arg } : arg
    let config: GraphQLRequestInit = {
      ...baseFetchOptions,
      method,
      signal,
      body,
      document,
      variables,
      ...rest,
    }

    config.headers = await prepareHeaders(
      new Headers(stripUndefined(headers)),
      
      { getState, endpoint, forced, type }
    )
    url = joinUrls(baseUrl, url)
 
    client = client ? client : new GraphQLClient(url);
    client.setHeaders(new Headers(config.headers));

    try {
      return { data: await client.request(document, variables), meta: {} };
    } catch (error) {
      if (error instanceof ClientError) {
        const { name, message, stack, request, response } = error;
        return { error: { name, message, stack }, meta: { request, response } };
      }
      throw error;
    }
  }
}




